import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { COMPANY } from "@/lib/data";

/**
 * Generic legal document modal for CGV / Privacy Policy / Mentions.
 * type: "cgv" | "privacy"
 */
export default function LegalDocsModal({ type, open, onClose }) {
  useEffect(() => {
    if (open) document.body.classList.add("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  const content = type === "cgv" ? CGV_CONTENT : PRIVACY_CONTENT;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-[#0F172A]/70 backdrop-blur-sm p-0 lg:p-8 flex items-end lg:items-center justify-center"
          onClick={onClose}
          data-testid={`${type}-modal`}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FDFBF7] w-full lg:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl lg:rounded-3xl border border-[#e2d9ce]"
          >
            <div className="sticky top-0 z-10 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#e2d9ce] px-6 lg:px-10 py-4 flex items-center justify-between">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                HMCD · {content.crumb}
              </div>
              <button
                data-testid={`${type}-close`}
                onClick={onClose}
                className="h-9 w-9 rounded-full bg-[#F5F1EA] hover:bg-[#0F172A] hover:text-[#FDFBF7] transition-colors flex items-center justify-center"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 lg:px-10 py-10 lg:py-14">
              <div className="eyebrow mb-3">{content.eyebrow}</div>
              <h2 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A]">
                {content.title}
              </h2>
              {content.intro && (
                <p className="mt-6 text-[15px] leading-relaxed text-[#334155]">
                  {content.intro}
                </p>
              )}

              {content.sections.map((s, i) => (
                <section key={i} className="mt-8">
                  <h3 className="font-serif-display text-xl lg:text-2xl text-[#0F172A] mb-3">
                    {s.title}
                  </h3>
                  {s.body.map((p, j) => (
                    <p key={j} className="text-[15px] leading-relaxed text-[#334155] mt-2">
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-2 space-y-1.5 text-[15px] text-[#334155]">
                      {s.list.map((it, k) => (
                        <li key={k} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#E65100] shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <div className="mt-12 pt-6 border-t border-[#e2d9ce] text-xs text-[#64748B]">
                Dernière mise à jour :{" "}
                {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })} ·
                Éditeur : {COMPANY.name} — {COMPANY.city}.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const CGV_CONTENT = {
  crumb: "Conditions Générales de Vente",
  eyebrow: "CGV · Version 2026",
  title: "Conditions Générales de Vente",
  intro:
    "Les présentes conditions générales de vente régissent les relations contractuelles entre HM Conseil Digital et ses clients, particuliers ou professionnels, pour toute prestation d'étude, fourniture, pose et maintenance d'équipements de rénovation énergétique.",
  sections: [
    {
      title: "1. Objet",
      body: [
        "HM Conseil Digital propose des prestations d'installation de systèmes photovoltaïques, d'isolation thermique par l'extérieur (ITE), de pompes à chaleur, de chauffe-eau thermodynamiques et de batteries de stockage. Toute commande implique l'acceptation sans réserve des présentes CGV.",
      ],
    },
    {
      title: "2. Devis et commande",
      body: [
        "Chaque prestation fait l'objet d'un devis détaillé et gratuit, valable 30 jours à compter de sa date d'émission. La commande est considérée comme ferme dès la signature du devis par le client et le versement d'un acompte de 30%.",
      ],
    },
    {
      title: "3. Prix et modalités de paiement",
      body: [
        "Les prix sont exprimés en euros, toutes taxes comprises (TVA 5,5% ou 10% selon éligibilité). Le solde est payable selon l'échéancier suivant :",
      ],
      list: [
        "30% à la signature du devis",
        "40% au démarrage du chantier",
        "30% à la mise en service et signature du procès-verbal de réception",
      ],
    },
    {
      title: "4. Délais et exécution",
      body: [
        "Les délais indicatifs sont précisés dans le devis. Les délais courent à compter de la date d'obtention des autorisations administratives éventuelles (permis, DP, accord de copropriété) et de l'encaissement de l'acompte.",
      ],
    },
    {
      title: "5. Garanties",
      body: [
        "HMCD couvre l'ensemble de ses installations par une garantie décennale souscrite auprès d'AXA Assurances Pro. Les équipements bénéficient en outre des garanties fabricants (25 ans en production pour les panneaux, 10 ans pour les batteries, 5 à 10 ans pour les onduleurs et PAC).",
      ],
    },
    {
      title: "6. Aides et financement",
      body: [
        "En tant que mandataire administratif et financier MaPrimeRénov', HMCD monte les dossiers d'aides publiques (MaPrimeRénov', CEE, éco-PTZ) pour ses clients éligibles. Le client s'engage à fournir les justificatifs nécessaires. En cas de refus d'aide non imputable à HMCD, le solde reste dû par le client.",
      ],
    },
    {
      title: "7. Rétractation",
      body: [
        "Conformément au Code de la consommation, tout client particulier dispose d'un délai de rétractation de 14 jours à compter de la signature du devis, sauf renonciation expresse pour une intervention urgente.",
      ],
    },
    {
      title: "8. Litiges",
      body: [
        "En cas de litige, une solution amiable sera recherchée en priorité. À défaut, le tribunal de commerce de Nanterre sera seul compétent. Le client peut également saisir gratuitement le médiateur de la consommation.",
      ],
    },
  ],
};

const PRIVACY_CONTENT = {
  crumb: "Politique de confidentialité",
  eyebrow: "Confidentialité · RGPD",
  title: "Politique de confidentialité",
  intro:
    "HM Conseil Digital s'engage à protéger la confidentialité et la sécurité des données personnelles de ses visiteurs et clients, en conformité avec le Règlement Général sur la Protection des Données (RGPD) et la loi Informatique et Libertés.",
  sections: [
    {
      title: "1. Responsable du traitement",
      body: [
        `Le responsable du traitement des données personnelles est HM Conseil Digital, siège social ${COMPANY.address}, ${COMPANY.city}. Pour toute question relative à vos données, contactez-nous à ${COMPANY.email}.`,
      ],
    },
    {
      title: "2. Données collectées",
      body: ["Nous collectons uniquement les données strictement nécessaires à la fourniture de nos services :"],
      list: [
        "Nom, prénom, email, téléphone (via le formulaire de contact/devis)",
        "Adresse et code postal (pour l'étude technique)",
        "Type de projet et informations techniques renseignées librement",
        "Données de navigation anonymisées (page vues, durée de session)",
      ],
    },
    {
      title: "3. Finalités",
      body: ["Vos données sont traitées pour :"],
      list: [
        "Répondre à votre demande de devis ou d'information",
        "Réaliser l'étude technique et le chiffrage",
        "Exécuter la prestation contractuelle",
        "Monter les dossiers d'aides publiques en votre nom",
        "Vous informer des évolutions de vos travaux (SAV, maintenance)",
      ],
    },
    {
      title: "4. Base légale",
      body: [
        "Le traitement est fondé sur votre consentement (formulaire de contact) et sur l'exécution du contrat pour nos clients.",
      ],
    },
    {
      title: "5. Durée de conservation",
      body: [
        "Les données de prospection sont conservées 3 ans après le dernier contact. Les données clients sont conservées 10 ans après la fin du contrat pour couvrir la garantie décennale, puis archivées ou supprimées.",
      ],
    },
    {
      title: "6. Destinataires",
      body: [
        "Vos données sont destinées exclusivement aux équipes de HM Conseil Digital et à ses sous-traitants techniques (fabricants, poseurs, comptable). Aucune donnée n'est vendue à des tiers ni transmise hors Union européenne.",
      ],
    },
    {
      title: "7. Vos droits RGPD",
      body: ["Vous disposez en permanence des droits suivants :"],
      list: [
        "Droit d'accès, de rectification et de suppression",
        "Droit d'opposition et de limitation du traitement",
        "Droit à la portabilité de vos données",
        "Droit d'introduire une réclamation auprès de la CNIL (cnil.fr)",
      ],
    },
    {
      title: "8. Cookies",
      body: [
        "Ce site utilise uniquement des cookies techniques strictement nécessaires à son fonctionnement. Aucun cookie de traçage publicitaire ou de tiers non essentiel n'est déposé sans votre consentement.",
      ],
    },
    {
      title: "9. Sécurité",
      body: [
        "Nos infrastructures sont hébergées sur des serveurs sécurisés en Union européenne, avec chiffrement TLS 1.3, sauvegardes chiffrées et contrôle d'accès strict.",
      ],
    },
  ],
};
